import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, CheckCircle2, Loader2, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", phone: "", email: "", product: "Perforated Cable Tray", quantity: "", message: "" };

export const Enquiry = () => {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");
  const [refId, setRefId] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const payload = {
        name: form.name,
        phone: form.phone,
        product: form.product,
        ...(form.email && { email: form.email }),
        ...(form.quantity && { quantity: form.quantity }),
        ...(form.message && { message: form.message }),
      };
      const { data } = await axios.post(`${API}/enquiries`, payload);
      setRefId(data.id.slice(0, 8).toUpperCase());
      setStatus("done");
      setForm(EMPTY);
      toast.success("Enquiry received. We'll call you back within 24 hours.");
    } catch (err) {
      setStatus("error");
      toast.error("Could not send your enquiry. Please call us directly.");
    }
  };

  return (
    <section id="contact" data-testid="enquiry-section" className="relative bg-ink py-20 md:py-28 border-t border-white/10 overflow-hidden">
      <div className="absolute inset-0 texture-perf opacity-40 pointer-events-none" />
      <div className="relative max-w-[1440px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-10">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="font-mono text-[10px] md:text-xs tracking-[0.35em] uppercase text-smoke mb-4">
              Contact Us
            </p>
            <h2 className="font-heading font-semibold uppercase text-3xl md:text-5xl tracking-wide text-white leading-tight">
              Get a Quote
            </h2>
            <p className="mt-6 max-w-md font-body text-sm md:text-base text-smoke leading-relaxed">
              Send your tray schedule or a rough sketch — we reply with a priced,
              manufacturable quote within one working day.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-10 flex flex-col gap-6">
              <a data-testid="contact-phone-1" href="tel:+919417021685" className="group flex items-center gap-4 w-fit">
                <span className="p-3 border border-white/15 group-hover:bg-white group-hover:text-ink text-white transition-colors duration-300"><Phone size={18} /></span>
                <span>
                  <span className="block font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Call / WhatsApp</span>
                  <span className="font-heading font-semibold text-2xl text-white tracking-wide">94170 21685</span>
                </span>
              </a>
              <a data-testid="contact-phone-2" href="tel:+919417601244" className="group flex items-center gap-4 w-fit">
                <span className="p-3 border border-white/15 group-hover:bg-white group-hover:text-ink text-white transition-colors duration-300"><Phone size={18} /></span>
                <span className="font-heading font-semibold text-2xl text-white tracking-wide pt-4">94176 01244</span>
              </a>
              <a data-testid="contact-email-link" href="mailto:dvsteels11@gmail.com" className="group flex items-center gap-4 w-fit">
                <span className="p-3 border border-white/15 group-hover:bg-white group-hover:text-ink text-white transition-colors duration-300"><Mail size={18} /></span>
                <span>
                  <span className="block font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Email</span>
                  <span className="font-heading font-semibold text-2xl text-white tracking-wide">dvsteels11@gmail.com</span>
                </span>
              </a>
              <div data-testid="contact-address" className="flex items-center gap-4">
                <span className="p-3 border border-white/15 text-white"><MapPin size={18} /></span>
                <span>
                  <span className="block font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Works</span>
                  <span className="font-body text-base text-white">F008 Industrial Growth, Ranipur, Pathankot — Punjab</span>
                </span>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            <div className="bg-white text-ink p-7 md:p-10 shadow-[0_30px_60px_rgba(0,0,0,0.4)] relative">
              <div className="absolute inset-0 texture-perf-light opacity-50 pointer-events-none" />
              {status === "done" ? (
                <div data-testid="enquiry-success" className="relative py-14 text-center">
                  <CheckCircle2 size={56} className="mx-auto text-royal" strokeWidth={1.5} />
                  <h3 className="mt-6 font-heading font-semibold uppercase text-3xl md:text-4xl text-ink">
                    Enquiry Logged.
                  </h3>
                  <p className="mt-3 font-body text-base text-ink/70">
                    Reference <span className="font-mono font-semibold text-royal">DVS-{refId}</span> — our team will
                    call you back within 24 working hours.
                  </p>
                  <button
                    data-testid="enquiry-again-button"
                    onClick={() => setStatus("idle")}
                    className="mt-8 btn-sweep btn-navy font-heading font-bold uppercase tracking-wider text-lg px-8 py-3"
                  >
                    Send Another
                  </button>
                </div>
              ) : (
                <form data-testid="enquiry-form" onSubmit={submit} className="relative grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-7">
                  <div>
                    <label htmlFor="enquiry-name" className="font-mono text-[10px] tracking-[0.3em] uppercase text-royal">Name *</label>
                    <input id="enquiry-name" data-testid="enquiry-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder="Your full name" className="input-line" />
                  </div>
                  <div>
                    <label htmlFor="enquiry-phone" className="font-mono text-[10px] tracking-[0.3em] uppercase text-royal">Phone *</label>
                    <input id="enquiry-phone" data-testid="enquiry-phone-input" required minLength={8} value={form.phone} onChange={set("phone")} placeholder="+91 ..." className="input-line" />
                  </div>
                  <div>
                    <label htmlFor="enquiry-email" className="font-mono text-[10px] tracking-[0.3em] uppercase text-royal">Email</label>
                    <input id="enquiry-email" data-testid="enquiry-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className="input-line" />
                  </div>
                  <div>
                    <label htmlFor="enquiry-product" className="font-mono text-[10px] tracking-[0.3em] uppercase text-royal">Product Interest</label>
                    <select id="enquiry-product" data-testid="enquiry-product-select" value={form.product} onChange={set("product")} className="input-line">
                      <option>Perforated Cable Tray</option>
                      <option>Ladder Type Tray</option>
                      <option>Accessories & Fittings</option>
                      <option>Custom Fabrication</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="enquiry-quantity" className="font-mono text-[10px] tracking-[0.3em] uppercase text-royal">Approx. Quantity</label>
                    <input id="enquiry-quantity" data-testid="enquiry-quantity-input" value={form.quantity} onChange={set("quantity")} placeholder="e.g. 600 m of 300 mm tray + fittings" className="input-line" />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="enquiry-message" className="font-mono text-[10px] tracking-[0.3em] uppercase text-royal">Message</label>
                    <textarea id="enquiry-message" data-testid="enquiry-message-input" rows={3} value={form.message} onChange={set("message")} placeholder="Sizes, finish, site location, timeline..." className="input-line resize-none" />
                  </div>
                  {status === "error" && (
                    <p data-testid="enquiry-error" className="sm:col-span-2 font-mono text-xs tracking-widest uppercase text-red-700">
                      Submission failed — please call 94170 21685 instead.
                    </p>
                  )}
                  <div className="sm:col-span-2 pt-2">
                    <button
                      data-testid="enquiry-submit-button"
                      type="submit"
                      disabled={status === "sending"}
                      className="btn-sweep btn-navy font-heading font-semibold uppercase tracking-wider text-lg px-10 py-3.5 flex items-center gap-2 disabled:opacity-60"
                    >
                      {status === "sending" ? (
                        <>Sending <Loader2 size={20} className="animate-spin" /></>
                      ) : (
                        <>Send Enquiry <ArrowUpRight size={20} strokeWidth={2.5} /></>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
